import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import {
	editModGroupRestaurantPropertiesInitialState,
	EditModGroupRestaurantPropertiesContext,
	TTypeOfModalWindow,
} from './editModGroupRestaurantPropertiesContext';
import { IAppContext, AppContext } from 'appState/appContext';
import { editModGroupRestaurantPropertiesReducer } from './editModGroupRestaurantPropertiesReducer';
import { EditModGroupRestaurantPropertiesActionTypes } from './editModGroupRestaurantPropertiesActions';
import {
	UserRoles,
	IGetModifierGroupInfoV2,
	IUpdateModGroupRestaurantProperties,
	IGetModGroupRestaurantProperties,
	IGetModGroupRestaurantPropertiesArguments,
	IServerError,
	IModGroupChildItem,
	ICircularReferenceValidationError,
} from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { getModifierGroupInfoV2, getModGroupRestaurantProperties } from 'backend/modifierGroups';
import { useFetchData } from 'hooks/useFetchData';

export const EditModGroupRestaurantPropertiesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(
		editModGroupRestaurantPropertiesReducer,
		editModGroupRestaurantPropertiesInitialState,
	);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const modGroupID = params.get('modifierGroupID');

	const { response: modGroupInfoResponse, loading: modGroupInfoLoading } = useFetchData<
		IGetModifierGroupInfoV2,
		string
	>(modGroupID ? getModifierGroupInfoV2 : null, modGroupID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetModGroupRestaurantProperties, IGetModGroupRestaurantPropertiesArguments>(
		state.IDSelectedRestaurant ? getModGroupRestaurantProperties : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			modGroupId: state.modGroupId,
		},
	);

	useEffect(() => {
		modGroupInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [modGroupInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (modGroupInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const fetchedRestaurants = hasAccess
				? modGroupInfoResponse.restaurants
				: modGroupInfoResponse.restaurants.filter((restaurant) => userRestaurantIDs.includes(restaurant.id));

			dispatch({
				type: EditModGroupRestaurantPropertiesActionTypes.SET_ALL_DATA,
				payload: {
					modGroupId: modGroupInfoResponse.id,
					modGroupName: modGroupInfoResponse.name || '',
					customerId: modGroupInfoResponse.customer.id || '',
					customerName: modGroupInfoResponse.customer.name || '',
					fetchedRestaurants,
				},
			});
		}
	}, [modGroupID, loggedUser.restaurants, hasAccess, modGroupInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: EditModGroupRestaurantPropertiesActionTypes.SET_DATA_FROM_RESTAURANT,
				payload: {
					forceDisable: restaurantPropertiesResponse.forceDisable,
					isPortionFlow: restaurantPropertiesResponse.isPortionFlow,
					parents: restaurantPropertiesResponse.parents,
					children: restaurantPropertiesResponse.children,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: EditModGroupRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.IDSelectedRestaurant]);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: EditModGroupRestaurantPropertiesActionTypes.SET_ID_RESTAURANT,
				payload: {
					IDSelectedRestaurant: value,
					forceDisable: value ? state.forceDisable : false,
					isPortionFlow: value ? state.isPortionFlow : false,
					parents: value ? state.parents : [],
					children: value ? state.children : [],
				},
			});
		},
		[state.forceDisable, state.isPortionFlow, state.parents, state.children],
	);

	const toggleForceDisable = useCallback(() => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.TOGGLE_FORCE_DISABLE,
		});
	}, []);

	const setChildren = useCallback((value: IModGroupChildItem[]) => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.SET_CHILDREN,
			payload: {
				children: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateModGroupRestaurantProperties>) => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: Nullable<IServerError<ICircularReferenceValidationError>>) => {
		dispatch({
			type: EditModGroupRestaurantPropertiesActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	return (
		<EditModGroupRestaurantPropertiesContext.Provider
			value={{
				refetchRestPropsData,
				setIDRestaurant,
				toggleForceDisable,
				setChildren,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				customerId: state.customerId,
				customerName: state.customerName,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				modGroupId: state.modGroupId,
				modGroupName: state.modGroupName,
				forceDisable: state.forceDisable,
				isPortionFlow: state.isPortionFlow,
				parents: state.parents,
				children: state.children,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditModGroupRestaurantPropertiesContext.Provider>
	);
};
